/* DilBudgetMeter — постоянна horizontal progress bar в top navbar.
 *
 * Показва DIL консумирани vs daily budget с color-coded fill:
 *   зелено < 50% · amber 50-80% · червено > 80%
 *
 * Обновява се на meal-added / meal-log-changed events + every 60s poll.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Activity } from "lucide-react";

export default function DilBudgetMeter({ compact = false }) {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const [state, setState] = useState(null);

  const load = useCallback(async () => {
    try {
      // Sync с dashboard DIL card: показваме БУФЕРИРАН (effective) DIL,
      // а не суров. /meals/today връща `daily_effective_dil`,
      // /dil/suggestions — `daily_dil_budget` (personalized limit).
      const [sugRes, mealsRes] = await Promise.all([
        axios.get(`${API}/dil/suggestions`).catch(() => null),
        axios.get(`${API}/meals/today`).catch(() => null),
      ]);
      const budget = Number(sugRes?.data?.daily_dil_budget) || 0;
      const effectiveConsumed = Number(mealsRes?.data?.daily_effective_dil) || 0;
      const remaining = Math.max(0, budget - effectiveConsumed);
      setState({ budget, remaining, consumed: effectiveConsumed });
    } catch {
      setState(null);
    }
  }, [API]);

  useEffect(() => {
    load();
    const onChange = () => load();
    window.addEventListener("meal-added", onChange);
    window.addEventListener("meal-log-changed", onChange);
    window.addEventListener("profile-updated", onChange);
    // Light 60s poll as belt-and-suspenders for multi-tab syncing
    const id = setInterval(load, 60_000);
    return () => {
      window.removeEventListener("meal-added", onChange);
      window.removeEventListener("meal-log-changed", onChange);
      window.removeEventListener("profile-updated", onChange);
      clearInterval(id);
    };
  }, [load]);

  if (!state || !state.budget) return null;

  const pct = Math.min(100, (state.consumed / state.budget) * 100);
  const color = pct > 80 ? "#EF4444" : pct > 50 ? "#F59E0B" : "#10B981";
  const labelText = pct > 80 ? (bg ? "високо" : "high") : pct > 50 ? (bg ? "умерено" : "moderate") : (bg ? "в норма" : "on track");

  return (
    <div
      className={`flex items-center gap-2 ${compact ? "min-w-[150px]" : "min-w-[220px]"} ${isDark ? "text-gray-300" : "text-gray-700"}`}
      data-testid="dil-budget-meter"
      title={bg
        ? `Инсулинов бюджет: ${state.consumed.toFixed(1)} от ${Math.round(state.budget)} · остават ${state.remaining.toFixed(1)}. DIL показва колко инсулин-стимулираща храна си приел/а днес.`
        : `Insulin budget: ${state.consumed.toFixed(1)} of ${Math.round(state.budget)} · ${state.remaining.toFixed(1)} remaining. DIL shows insulin-stimulating food eaten today.`}
    >
      <Activity className="w-3.5 h-3.5 shrink-0" style={{ color }} />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2 text-[10px] leading-none mb-1">
          <span className="font-semibold">
            {compact
              ? <>{state.consumed.toFixed(1)}<span className="opacity-60">/{Math.round(state.budget)}</span></>
              : <>{bg ? "Бюджет " : "Budget "}{state.consumed.toFixed(1)}<span className="opacity-60"> от {Math.round(state.budget)}</span></>
            }
          </span>
          {!compact && (
            <span className="font-medium tabular-nums" style={{ color }}>
              {labelText}
            </span>
          )}
        </div>
        <div className={`relative h-1.5 rounded-full overflow-hidden ${isDark ? "bg-white/5" : "bg-gray-200"}`}>
          <div
            className="absolute inset-y-0 left-0 rounded-full transition-all duration-500"
            style={{ width: `${Math.max(3, pct)}%`, backgroundColor: color }}
          />
        </div>
      </div>
    </div>
  );
}
